import { formatApplyTypeLabel, formatConditionLabel } from '~/components/Vouchers/voucherConditionFormatters';

import { AuditLog } from '~/types/responses';

import currencyFormatter from '~/utils/currencyFormatter';
import { humanize } from '~/utils/humanize';
import { isRecord } from '~/utils/isRecord';

const MAX_GENERIC_FIELDS = 6;
const MAX_UPDATED_ROW_FIELDS = 3;
const ROW_AUDIT_META_FIELDS = new Set([
  'id',
  'created_at',
  'updated_at',
  'modified_by',
  'created_by',
  'isNew',
  'config',
]);

type AuditDiff = NonNullable<NonNullable<AuditLog['model']>['diff']>;
type AuditRow = Record<string, unknown>;
type RowUpdateDescription = {
  detail: string;
  coveredFields?: Array<string>;
};

export type AuditEventValueChange = {
  field: string;
  previousValue: string;
  nextValue: string;
};

type ArrayFieldSummaryConfig = {
  field: string;
  title: string;
  itemLabel: string;
  idFields?: Array<string>;
  describeRow?: (row: AuditRow) => string | null;
  describeUpdatedRow?: (
    oldRow: AuditRow,
    newRow: AuditRow,
    changedFields: Array<string>,
  ) => RowUpdateDescription | null;
  describeItems?: (items: Array<unknown>) => string | null;
};

type ScalarFieldSummaryConfig = {
  field: string;
  title: string;
  actionLabel: string;
  itemLabel?: string;
  describeItems?: (items: Array<unknown>) => string | null;
};

const CREATED_VOUCHER_SUMMARY_FIELDS = new Set([
  'reward_kind',
  'voucher_name',
  'voucher_title',
  'product_type',
  'offer_id',
  'voucher_conditions',
]);

function describeConditionRow(row: AuditRow) {
  const conditionType = row.condition_type;
  if (typeof conditionType !== 'string' || conditionType.trim() === '') {
    return null;
  }

  const conditionValue = formatConditionAuditValue(row.condition_value);
  const conditionApplyType = formatConditionAuditApplyType(row.condition_apply_type);

  return `${formatSummaryConditionLabel(conditionType)} condition (value ${conditionValue}, apply type ${conditionApplyType})`;
}

function describeVoucherRewardRow(row: AuditRow, rewardKind?: string | null, productType?: string | null) {
  const amount = formatAuditCurrencyAmount(row.reward_amount, row.currency_code);
  if (!amount) {
    const productType = asString(row.product_type);
    return productType ? `for ${humanize(productType)}` : null;
  }

  const minSpend = asFiniteNumber(row.min_spend);
  const scope = formatCreatedVoucherProductScope(row, productType ?? asString(row.product_type));
  const parts = [rewardKind === 'provide_credit' ? `${amount} credit` : `up to ${amount} off`];

  if (minSpend !== null) {
    parts.push(
      scope
        ? `min spend ${formatAuditCurrencyAmount(minSpend, row.currency_code)} on ${scope}`
        : `min spend ${formatAuditCurrencyAmount(minSpend, row.currency_code)}`,
    );
  } else if (scope) {
    parts.push(`for ${scope}`);
  }

  return parts.join(', ');
}

function describeUpdatedVoucherRewardRow(
  oldRow: AuditRow,
  newRow: AuditRow,
  changedFields: Array<string>,
): RowUpdateDescription | null {
  const rowDescription = describeVoucherRewardRow(newRow);
  if (!rowDescription) {
    return null;
  }

  const detailParts: Array<string> = [rowDescription];
  const coveredFields: Array<string> = [];

  if (changedFields.includes('reward_amount')) {
    const nextAmount = formatAuditCurrencyAmount(newRow.reward_amount, newRow.currency_code);
    const previousAmount = formatAuditCurrencyAmount(oldRow.reward_amount, oldRow.currency_code);
    if (nextAmount && previousAmount) {
      detailParts.push(`was ${previousAmount}`);
    }
    coveredFields.push('reward_amount');
  }

  if (changedFields.includes('min_spend')) {
    coveredFields.push('min_spend');
  }

  if (changedFields.includes('min_spend_products')) {
    coveredFields.push('min_spend_products');
  }

  return {
    detail: detailParts.join(', '),
    coveredFields,
  };
}

function getFirstConditionRow(diff: AuditDiff | undefined) {
  const addedConditions = asRows(diff?.added?.voucher_conditions);
  if (addedConditions && addedConditions.length > 0) {
    return addedConditions[0];
  }

  const updatedConditions = getChangeValues(diff?.updated?.voucher_conditions);
  const newConditions = asRows(updatedConditions?.newValue);
  if (newConditions && newConditions.length > 0) {
    return newConditions[0];
  }

  return null;
}

function formatConditionAuditValue(value: unknown) {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue === '' ? 'blank' : JSON.stringify(trimmedValue);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (value === null || value === undefined) {
    return 'blank';
  }

  return stableStringify(value);
}

function formatConditionAuditApplyType(value: unknown) {
  if (typeof value !== 'string' || value.trim() === '') {
    return formatApplyTypeLabel('NONE');
  }

  return formatApplyTypeLabel(value);
}

function formatSummaryConditionLabel(value: string) {
  const label = formatConditionLabel(value);
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

function humanizeAuditToken(value: string) {
  return humanize(value.replace(/-/g, '_'));
}

function humanizeAuditField(value: string) {
  const initialisms = new Set(['api', 'id', 'ip', 'url']);

  return value
    .split('_')
    .filter(Boolean)
    .map((word) => {
      const normalizedWord = word.toLowerCase();
      if (initialisms.has(normalizedWord)) {
        return normalizedWord.toUpperCase();
      }

      return `${normalizedWord.charAt(0).toUpperCase()}${normalizedWord.slice(1)}`;
    })
    .join(' ');
}

function formatAuditCurrencyAmount(amount: unknown, currencyCode: unknown) {
  const numericAmount = asFiniteNumber(amount);
  if (numericAmount === null) {
    return null;
  }

  const normalizedCurrencyCode = asString(currencyCode) ?? 'AUD';
  const maximumFractionDigits = Number.isInteger(numericAmount) ? 0 : 2;
  return currencyFormatter(normalizedCurrencyCode, numericAmount, maximumFractionDigits);
}

function describeUpdatedRewardRow(
  oldRow: AuditRow,
  newRow: AuditRow,
  changedFields: Array<string>,
): RowUpdateDescription | null {
  if (!changedFields.includes('reward_amount')) {
    return null;
  }

  const nextAmount = formatAuditCurrencyAmount(newRow.reward_amount, newRow.currency_code);
  const previousAmount = formatAuditCurrencyAmount(oldRow.reward_amount, oldRow.currency_code);
  if (!nextAmount && !previousAmount) {
    return null;
  }

  if (nextAmount && previousAmount) {
    return {
      detail: `${nextAmount}, was ${previousAmount}`,
      coveredFields: ['reward_amount'],
    };
  }

  return {
    detail: nextAmount ?? `was ${previousAmount}`,
    coveredFields: ['reward_amount'],
  };
}

function formatNamedItemSummary(labels: Array<string>) {
  if (labels.length === 0) {
    return null;
  }

  const [firstLabel, ...remainingLabels] = labels;
  return remainingLabels.length > 0 ? `${firstLabel} and ${remainingLabels.length} more` : firstLabel;
}

function describeToggleItems(items: Array<unknown>) {
  const labels = items
    .map((item) => (typeof item === 'string' && item.trim() !== '' ? humanizeAuditToken(item.trim()) : null))
    .filter((item): item is string => Boolean(item));

  return formatNamedItemSummary(labels);
}

function asDisplayString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function describeVoucherUserItem(item: unknown) {
  if (!isRecord(item)) {
    return asDisplayString(item);
  }

  return (
    asDisplayString(item.email) ??
    asDisplayString(item.user_email) ??
    asDisplayString(item.userId) ??
    asDisplayString(item.user_id) ??
    asDisplayString(item.id_member) ??
    asDisplayString(item.id_user) ??
    asDisplayString(item.fk_user) ??
    asDisplayString(item.id)
  );
}

function describeVoucherUserItems(items: Array<unknown>) {
  const labels = items.map(describeVoucherUserItem).filter((item): item is string => Boolean(item));
  return formatNamedItemSummary(labels);
}

const ARRAY_FIELD_SUMMARY_CONFIGS: Array<ArrayFieldSummaryConfig> = [
  {
    field: 'voucher_rewards',
    title: 'Rewards',
    itemLabel: 'reward row',
    idFields: ['id_voucher_reward'],
    describeRow: describeVoucherRewardRow,
    describeUpdatedRow: describeUpdatedVoucherRewardRow,
  },
  {
    field: 'voucher_conditions',
    title: 'Voucher Conditions',
    itemLabel: 'condition row',
    idFields: ['id_voucher_condition'],
    describeRow: describeConditionRow,
  },
  {
    field: 'voucher_toggles',
    title: 'Voucher Toggles',
    itemLabel: 'toggle',
    describeItems: describeToggleItems,
  },
  {
    field: 'promo_discounts',
    title: 'Discounts',
    itemLabel: 'discount row',
    idFields: ['id_promo_code_discount', 'id'],
    describeRow: (row) => {
      const discountValue = asFiniteNumber(row.discount_value);
      if (discountValue === null) {
        return null;
      }

      const minSpend = asFiniteNumber(row.min_spend);
      const parts = [`discount value ${discountValue}`];
      if (minSpend !== null) {
        parts.push(`min spend ${formatAuditCurrencyAmount(minSpend, row.currency_code) ?? minSpend}`);
      }
      return parts.join(', ');
    },
  },
  {
    field: 'promo_conditions',
    title: 'Promo Conditions',
    itemLabel: 'condition row',
    idFields: ['id_promo_code_condition', 'id'],
    describeRow: describeConditionRow,
  },
];

const SCALAR_FIELD_SUMMARY_CONFIGS: Array<ScalarFieldSummaryConfig> = [
  {
    field: 'promo_users_created_count',
    title: 'Promo Users',
    actionLabel: 'added',
    itemLabel: 'user',
  },
  {
    field: 'promo_users_created',
    title: 'Promo Users',
    actionLabel: 'added',
    itemLabel: 'user',
  },
  {
    field: 'voucher_users_created',
    title: 'Voucher Users',
    actionLabel: 'created',
    itemLabel: 'voucher user',
    describeItems: describeVoucherUserItems,
  },
];

function asRows(value: unknown): Array<AuditRow> | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.filter(isRecord);
}

function asArray(value: unknown): Array<unknown> | null {
  return Array.isArray(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringList(value: unknown): Array<string> {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '').map((item) => item.trim())
    : [];
}

function getChangedFields(log: AuditLog): Array<string> {
  const diff = log.model?.diff;
  const fieldNames = new Set<string>();

  Object.keys(diff?.added ?? {}).forEach((field) => fieldNames.add(field));
  Object.keys(diff?.updated ?? {}).forEach((field) => fieldNames.add(field));
  (diff?.deleted ?? []).forEach((field) => fieldNames.add(field));

  return Array.from(fieldNames);
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function formatFieldList(fields: Array<string>, limit = MAX_GENERIC_FIELDS) {
  const visibleFields = fields.slice(0, limit).map((field) => humanizeAuditField(field));
  const moreCount = fields.length - visibleFields.length;

  return `${visibleFields.join(', ')}${moreCount > 0 ? ` and ${moreCount} more` : ''}`;
}

function getRowKey(row: AuditRow, index: number, idFields: Array<string>) {
  const matchingIdField = idFields.find((field) => {
    const value = row[field];
    return typeof value === 'string' && value.trim() !== '';
  });

  return matchingIdField ? (row[matchingIdField] as string) : `index:${index}`;
}

function valuesEqual(left: unknown, right: unknown) {
  if (Array.isArray(left) || Array.isArray(right) || isRecord(left) || isRecord(right)) {
    return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
  }

  return left === right;
}

function getUpdatedRowFields(oldRow: AuditRow, newRow: AuditRow, ignoredFields: Array<string>): Array<string> {
  const ignoredFieldSet = new Set([...ROW_AUDIT_META_FIELDS, ...ignoredFields]);
  const keys = new Set([...Object.keys(oldRow), ...Object.keys(newRow)]);

  return Array.from(keys).filter((key) => !ignoredFieldSet.has(key) && !valuesEqual(oldRow[key], newRow[key]));
}

function getChangeValues(change: unknown): { oldValue: unknown; newValue: unknown } | null {
  if (!isRecord(change)) {
    return null;
  }

  if ('old' in change || 'new' in change) {
    return {
      oldValue: change.old,
      newValue: change.new,
    };
  }

  if ('before' in change || 'after' in change) {
    return {
      oldValue: change.before,
      newValue: change.after,
    };
  }

  return null;
}

function formatAuditValue(value: unknown): string {
  if (value === undefined) {
    return 'unset';
  }

  if (value === null) {
    return 'null';
  }

  const stringValue = typeof value === 'string' ? value : stableStringify(value);
  return stringValue.slice(0, 6);
}

function isScalarAuditValue(value: unknown): value is string | number | boolean | null | undefined {
  return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value);
}

/**
 * The audit API supplies the actual before/after values under `updated`.
 * Keep them separate from the prose summary so the UI can render the prior
 * value semantically (rather than embedding markup in a string).
 */
export function getAuditEventValueChanges(log: AuditLog): Array<AuditEventValueChange> {
  const updated = log.model?.diff?.updated;
  if (!isRecord(updated)) {
    return [];
  }

  return Object.entries(updated).flatMap(([field, change]) => {
    const values = getChangeValues(change);
    if (!values || !isScalarAuditValue(values.oldValue) || !isScalarAuditValue(values.newValue)) {
      return [];
    }

    return [
      {
        field: humanizeAuditField(field),
        previousValue: formatAuditValue(values.oldValue),
        nextValue: formatAuditValue(values.newValue),
      },
    ];
  });
}

function stableStringify(value: unknown) {
  return JSON.stringify(value ?? null);
}

function getArrayItemDeltas(oldItems: Array<unknown>, newItems: Array<unknown>) {
  const oldCounts = new Map<string, number>();
  const newCounts = new Map<string, number>();

  oldItems.forEach((item) => {
    const key = stableStringify(item);
    oldCounts.set(key, (oldCounts.get(key) ?? 0) + 1);
  });
  newItems.forEach((item) => {
    const key = stableStringify(item);
    newCounts.set(key, (newCounts.get(key) ?? 0) + 1);
  });

  const remainingOldCounts = new Map(oldCounts);
  const addedItems: Array<unknown> = [];
  newItems.forEach((item) => {
    const key = stableStringify(item);
    const remainingOldCount = remainingOldCounts.get(key) ?? 0;
    if (remainingOldCount > 0) {
      remainingOldCounts.set(key, remainingOldCount - 1);
      return;
    }
    addedItems.push(item);
  });

  const remainingNewCounts = new Map(newCounts);
  const removedItems: Array<unknown> = [];
  oldItems.forEach((item) => {
    const key = stableStringify(item);
    const remainingNewCount = remainingNewCounts.get(key) ?? 0;
    if (remainingNewCount > 0) {
      remainingNewCounts.set(key, remainingNewCount - 1);
      return;
    }
    removedItems.push(item);
  });

  return {
    addedItems,
    removedItems,
  };
}

function getCreatedVoucherRewardRow(diff: AuditDiff | undefined): AuditRow | null {
  const addedRewards = asRows(diff?.added?.voucher_rewards);
  if (addedRewards && addedRewards.length > 0) {
    return addedRewards[0];
  }

  const updatedRewards = getChangeValues(diff?.updated?.voucher_rewards);
  const newRewards = asRows(updatedRewards?.newValue);
  if (newRewards && newRewards.length > 0) {
    return newRewards[0];
  }

  return null;
}

function formatCreatedVoucherRewardAmount(rewardRow: AuditRow | null, rewardKind: string | null) {
  if (!rewardRow) {
    return null;
  }

  const formattedAmount = formatAuditCurrencyAmount(rewardRow.reward_amount, rewardRow.currency_code);
  if (!formattedAmount) {
    return null;
  }

  if (rewardKind === 'provide_credit') {
    return `${formattedAmount} credit`;
  }

  return `up to ${formattedAmount} off`;
}

function formatCreatedVoucherProductScope(rewardRow: AuditRow | null, productType: string | null) {
  const minSpendProducts = rewardRow ? asStringList(rewardRow.min_spend_products) : [];
  if (minSpendProducts.length > 0) {
    const [firstProduct, ...remainingProducts] = minSpendProducts;
    return `${firstProduct}${remainingProducts.length > 0 ? ` and ${remainingProducts.length} more` : ''}`;
  }

  return productType ? humanize(productType) : null;
}

function formatCreatedVoucherMinSpend(rewardRow: AuditRow | null, productType: string | null) {
  if (!rewardRow) {
    return productType ? `for ${humanize(productType)}` : null;
  }

  const minSpend = asFiniteNumber(rewardRow.min_spend);
  const scope = formatCreatedVoucherProductScope(rewardRow, productType);
  if (minSpend === null) {
    return scope ? `for ${scope}` : null;
  }

  const formattedMinSpend = formatAuditCurrencyAmount(minSpend, rewardRow.currency_code);
  if (!formattedMinSpend) {
    return scope ? `for ${scope}` : null;
  }

  return scope ? `min spend ${formattedMinSpend} on ${scope}` : `min spend ${formattedMinSpend}`;
}

function formatCreatedVoucherOfferId(offerId: unknown) {
  const normalizedOfferId = asString(offerId);
  return normalizedOfferId ? `offer ID ${normalizedOfferId}` : null;
}

function getCreatedVoucherSummary(log: AuditLog): string | null {
  if (log.action !== 'created' || log.model?.type !== 'voucher' || !isRecord(log.model?.diff?.added)) {
    return null;
  }

  const addedFields = log.model.diff.added as Record<string, unknown>;
  const rewardKind = asString(addedFields.reward_kind);
  const voucherName = asString(addedFields.voucher_name) ?? asString(addedFields.voucher_title);
  const productType = asString(addedFields.product_type);
  const rewardRow = getCreatedVoucherRewardRow(log.model.diff);
  const conditionRow = getFirstConditionRow(log.model.diff);

  const detailParts = [
    rewardKind ? `reward kind ${humanize(rewardKind)}` : null,
    describeVoucherRewardRow(rewardRow, rewardKind, productType),
    voucherName,
    describeConditionRow(conditionRow ?? {}) ?? null,
    formatCreatedVoucherOfferId(addedFields.offer_id),
  ].filter((part): part is string => Boolean(part));

  if (detailParts.length === 0) {
    return null;
  }

  const extraFields = getChangedFields(log).filter(
    (field) => field !== 'voucher_rewards' && !CREATED_VOUCHER_SUMMARY_FIELDS.has(field),
  );

  return `Created Voucher with ${detailParts.join(', ')}${extraFields.length > 0 ? ` and ${extraFields.length} more` : ''}`;
}

function buildPrimitiveArrayFieldSummary(
  oldItems: Array<unknown>,
  newItems: Array<unknown>,
  { title, itemLabel, describeItems }: Pick<ArrayFieldSummaryConfig, 'title' | 'itemLabel' | 'describeItems'>,
) {
  const { addedItems, removedItems } = getArrayItemDeltas(oldItems, newItems);

  const parts: Array<string> = [];
  if (addedItems.length > 0) {
    const detail = describeItems?.(addedItems);
    parts.push(`added ${pluralize(addedItems.length, itemLabel)}${detail ? ` (${detail})` : ''}`);
  }
  if (removedItems.length > 0) {
    const detail = describeItems?.(removedItems);
    parts.push(`removed ${pluralize(removedItems.length, itemLabel)}${detail ? ` (${detail})` : ''}`);
  }

  if (parts.length === 0 && !valuesEqual(oldItems, newItems)) {
    parts.push(`updated ${pluralize(newItems.length, itemLabel)}`);
  }

  return parts.length > 0 ? `${title}: ${parts.join('; ')}` : null;
}

function buildArrayFieldSummary(
  oldItems: Array<unknown>,
  newItems: Array<unknown>,
  { title, itemLabel, idFields = [], describeRow, describeUpdatedRow, describeItems }: ArrayFieldSummaryConfig,
) {
  const oldRows = asRows(oldItems);
  const newRows = asRows(newItems);

  if (!oldRows || !newRows || idFields.length === 0) {
    return buildPrimitiveArrayFieldSummary(oldItems, newItems, { title, itemLabel, describeItems });
  }

  const oldByKey = new Map(oldRows.map((row, index) => [getRowKey(row, index, idFields), row]));
  const newByKey = new Map(newRows.map((row, index) => [getRowKey(row, index, idFields), row]));

  let addedCount = 0;
  let removedCount = 0;
  let updatedCount = 0;
  const addedRows: Array<AuditRow> = [];
  const removedRows: Array<AuditRow> = [];
  const updatedRows: Array<AuditRow> = [];
  const updatedRowChanges: Array<{ oldRow: AuditRow; newRow: AuditRow; changedFields: Array<string> }> = [];
  const updatedFields = new Set<string>();

  newByKey.forEach((newRow, key) => {
    const oldRow = oldByKey.get(key);
    if (!oldRow) {
      addedCount += 1;
      addedRows.push(newRow);
      return;
    }

    const changedFields = getUpdatedRowFields(oldRow, newRow, idFields);
    if (changedFields.length === 0) {
      return;
    }

    updatedCount += 1;
    updatedRows.push(newRow);
    updatedRowChanges.push({ oldRow, newRow, changedFields });
    changedFields.forEach((field) => updatedFields.add(field));
  });

  oldByKey.forEach((oldRow, key) => {
    if (!newByKey.has(key)) {
      removedCount += 1;
      removedRows.push(oldRow);
    }
  });

  function formatRowAction(actionLabel: string, count: number, rows: Array<AuditRow>, allowRowDescription = true) {
    if (allowRowDescription && count === 1 && describeRow && rows.length === 1) {
      const rowDescription = describeRow(rows[0]);
      if (rowDescription) {
        return `${actionLabel} ${rowDescription}`;
      }
    }

    return `${actionLabel} ${pluralize(count, itemLabel)}`;
  }

  const parts: Array<string> = [];
  if (addedCount > 0) {
    parts.push(formatRowAction('added', addedCount, addedRows));
  }
  if (updatedCount > 0) {
    const updatedRowDescription =
      updatedCount === 1 && updatedRowChanges.length === 1 && describeUpdatedRow
        ? describeUpdatedRow(
            updatedRowChanges[0].oldRow,
            updatedRowChanges[0].newRow,
            updatedRowChanges[0].changedFields,
          )
        : null;
    const coveredFields = new Set(updatedRowDescription?.coveredFields ?? []);
    const updatedFieldList = Array.from(updatedFields).filter((field) => !coveredFields.has(field));
    const updatedDetailsSuffix = updatedRowDescription ? ` (${updatedRowDescription.detail})` : '';
    const updatedFieldsSuffix =
      updatedFieldList.length > 0 ? ` (${formatFieldList(updatedFieldList, MAX_UPDATED_ROW_FIELDS)})` : '';
    parts.push(
      `${formatRowAction('updated', updatedCount, updatedRows, false)}${updatedDetailsSuffix}${updatedFieldsSuffix}`,
    );
  }
  if (removedCount > 0) {
    parts.push(formatRowAction('removed', removedCount, removedRows));
  }

  return parts.length > 0 ? `${title}: ${parts.join('; ')}` : null;
}

function getArrayFieldSummary(diff: AuditDiff | undefined, config: ArrayFieldSummaryConfig) {
  const updatedValue = diff?.updated?.[config.field];
  const updatedChange = getChangeValues(updatedValue);
  if (updatedChange) {
    const oldItems = asArray(updatedChange.oldValue);
    const newItems = asArray(updatedChange.newValue);

    if (oldItems && newItems) {
      return buildArrayFieldSummary(oldItems, newItems, config);
    }
  }

  const addedItems = asArray(diff?.added?.[config.field]);
  if (addedItems) {
    return buildArrayFieldSummary([], addedItems, config);
  }

  if ((diff?.deleted ?? []).includes(config.field)) {
    return `${config.title} removed`;
  }

  return null;
}

function getScalarCreatedCount(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.length;
  }
  return null;
}

function formatScalarFieldSummary(config: ScalarFieldSummaryConfig, count: number, items?: Array<unknown>) {
  const detail = items && items.length > 0 ? config.describeItems?.(items) : null;
  if (config.itemLabel) {
    return `${config.title}: ${config.actionLabel} ${pluralize(count, config.itemLabel)}${detail ? ` (${detail})` : ''}`;
  }

  return `${config.title}: ${config.actionLabel}${detail ? ` (${detail})` : ''}`;
}

function getScalarFieldSummary(diff: AuditDiff | undefined, config: ScalarFieldSummaryConfig) {
  const addedItems = asArray(diff?.added?.[config.field]);
  if (addedItems) {
    return formatScalarFieldSummary(config, addedItems.length, addedItems);
  }

  const addedCount = getScalarCreatedCount(diff?.added?.[config.field]);
  if (addedCount !== null) {
    return formatScalarFieldSummary(config, addedCount);
  }

  const updatedChange = getChangeValues(diff?.updated?.[config.field]);
  if (updatedChange) {
    const previousItems = asArray(updatedChange.oldValue);
    const nextItems = asArray(updatedChange.newValue);
    if (previousItems && nextItems) {
      const { addedItems: nextCreatedItems } = getArrayItemDeltas(previousItems, nextItems);
      if (nextCreatedItems.length > 0) {
        return formatScalarFieldSummary(config, nextCreatedItems.length, nextCreatedItems);
      }

      return `${config.title}: ${config.actionLabel}`;
    }

    const nextCount = getScalarCreatedCount(updatedChange.newValue);
    const previousCount = getScalarCreatedCount(updatedChange.oldValue) ?? 0;
    const createdCount = nextCount !== null ? Math.max(0, nextCount - previousCount) : null;
    if (createdCount !== null && createdCount > 0) {
      return formatScalarFieldSummary(config, createdCount);
    }
    return `${config.title}: ${config.actionLabel}`;
  }

  return null;
}

export function getAuditEventChangeSummary(log: AuditLog) {
  const createdVoucherSummary = getCreatedVoucherSummary(log);
  if (createdVoucherSummary) {
    return createdVoucherSummary;
  }

  const changedFields = getChangedFields(log);
  const collectionSummaries = ARRAY_FIELD_SUMMARY_CONFIGS.map((config) =>
    getArrayFieldSummary(log.model?.diff, config),
  ).filter((summary): summary is string => Boolean(summary));
  const hasPromoUserCount = getScalarFieldSummary(log.model?.diff, SCALAR_FIELD_SUMMARY_CONFIGS[0]) !== null;
  const scalarSummaries = SCALAR_FIELD_SUMMARY_CONFIGS.filter(
    (config) => config.field !== 'promo_users_created' || !hasPromoUserCount,
  )
    .map((config) => getScalarFieldSummary(log.model?.diff, config))
    .filter((summary): summary is string => Boolean(summary));
  const summarizedFields = new Set([
    ...ARRAY_FIELD_SUMMARY_CONFIGS.map((config) => config.field),
    ...SCALAR_FIELD_SUMMARY_CONFIGS.map((config) => config.field),
  ]);
  const genericFields = changedFields.filter((field) => !summarizedFields.has(field));
  const allSummaries = [...collectionSummaries, ...scalarSummaries];

  if (allSummaries.length > 0 && genericFields.length === 0) {
    return allSummaries.join('; ');
  }

  if (allSummaries.length > 0 && genericFields.length > 0) {
    return `${allSummaries.join('; ')}; Changed ${formatFieldList(genericFields)}`;
  }

  if (genericFields.length > 0 || changedFields.length > 0) {
    const fieldsToRender = genericFields.length > 0 ? genericFields : changedFields;
    return `Changed ${formatFieldList(fieldsToRender)}`;
  }

  return log.resource;
}
